import { FC, useMemo } from 'react';
import { Button, Grid, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useStyles } from './styles';

import openModeIcon from '../../../../assets/streaming/actions/ic-openModeWhite.svg';
import stopTurnIcon from '../../../../assets/streaming/actions/hangCall.png';
import raiseHandIcon from '../../../../assets/streaming/actions/reloadLogo.png';
import microIcon from '../../../../assets/streaming/actions/ic-streaming-mic-off.svg';
import cameraIcon from '../../../../assets/streaming/actions/ic-camera-off.svg';
import InstructionRow from './parts/InstructionRow';

import { CurrentUserReducer, RootReducer, StreamingReducer } from '../../../../models/Redux';
import { thunkJoinEvent, thunkStartEvent } from '../../../../store/actions/Streaming.actions';

interface IntroProps {
    mobileLayout?: boolean;
}

const Intro: FC<IntroProps> = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const {
        user: { user_ide: userIde },
    } = useSelector<RootReducer, CurrentUserReducer>(state => state.currentUser);
    const {
        eventData: {
            event: { event_title: eventTitle },
            user: { user_ide: influencerUserIde },
        },
    } = useSelector<RootReducer, StreamingReducer>(state => state.streaming);

    const isEventInfluencer = useMemo(() => influencerUserIde === userIde, [influencerUserIde, userIde]);

    const renderInfluencerInstructions = () => {
        return (
            <>
                <InstructionRow
                    icon={openModeIcon}
                    altText={t('Open mode icon')}
                    title={t('Open Mode')}
                    text={t('Allow all your viewers to speak')}
                />
                <InstructionRow
                    icon={raiseHandIcon}
                    altText={t('Raise Hand icon')}
                    title={t('Raise Hand')}
                    text={t('Allow your viewers to ask for a turn to speak')}
                />
                <InstructionRow
                    icon={stopTurnIcon}
                    altText={t('Stop viewer turn icon')}
                    title={t('Stop viewer turn')}
                    text={t("Finish your viewer's turn")}
                />
                <InstructionRow
                    icon={cameraIcon}
                    altText={t('Camera off icon')}
                    title={t('Camera On/Off')}
                    text={t('Enable or disable your camera')}
                />
                <InstructionRow
                    icon={microIcon}
                    altText={t('Microphone On/Off icon')}
                    title={t('Microphone On/Off')}
                    text={t('Enable or disable your microphone')}
                />
            </>
        );
    };

    const renderViewerInstructions = () => {
        return (
            <>
                <InstructionRow
                    icon={openModeIcon}
                    altText={t('Open mode icon')}
                    title={t('Open Mode')}
                    text={t('Allow to speak with all')}
                />
                <InstructionRow
                    icon={raiseHandIcon}
                    altText={t('Raise Hand icon')}
                    title={t('Raise Hand')}
                    text={t('Ask for a turn to speak enabling your camera')}
                />
                <InstructionRow
                    icon={cameraIcon}
                    altText={t('Camera off icon')}
                    title={t('Camera On/Off')}
                    text={t('Enable or disable your camera')}
                />
                <InstructionRow
                    icon={microIcon}
                    altText={t('Microphone On/Off icon')}
                    title={t('Microphone On/Off')}
                    text={t('Enable or disable your microphone')}
                />
            </>
        );
    };

    const renderInstructions = () => {
        return isEventInfluencer ? renderInfluencerInstructions() : renderViewerInstructions();
    };

    const errorStartingStreaming = () => {
        enqueueSnackbar(
            t(
                'There was an error starting the streaming. Check the event date is correct and check your permissions for this browser.',
            ),
            { variant: 'error', autoHideDuration: 10000 },
        );

        setTimeout(() => {
            window.location.reload();
        }, 10000);
    };

    const errorJoiningStreaming = () => {
        enqueueSnackbar(
            t(
                'There was an error joining the streaming. Check if the event has started and check your permissions for this browser.',
            ),
            { variant: 'error', autoHideDuration: 10000 },
        );

        setTimeout(() => {
            window.location.reload();
        }, 10000);
    };

    const startOrJoinStreaming = () => {
        if (isEventInfluencer) {
            dispatch(thunkStartEvent(() => {}, errorStartingStreaming));
        } else {
            dispatch(thunkJoinEvent(() => {}, errorJoiningStreaming));
        }
    };

    return (
        <Grid
            container
            xs={12}
            className={classes.instructionsWrapper}
            justify="center"
            alignContent="center"
            alignItems="center"
        >
            <Grid
                justify="center"
                alignContent="center"
                alignItems="center"
                direction="column"
                container
                xs={8}
            >
                <Typography
                    className={clsx(classes.text, classes.instructionsTitle, classes.textAlignCenter)}
                >
                    {isEventInfluencer
                        ? t('Welcome to your Live Event')
                        : t('Welcome to {{eventName}}', { eventName: eventTitle })}
                </Typography>
                <Typography
                    className={clsx(classes.text, classes.instructionsSubTitle, classes.textAlignCenter)}
                >
                    {t('Check all the features before starting!')}
                </Typography>

                {renderInstructions()}

                <Grid container xs={12} justify="center" alignContent="center" alignItems="center">
                    <Button
                        variant="contained"
                        className={clsx(classes.mainButton)}
                        onClick={startOrJoinStreaming}
                    >
                        <Typography className={clsx(classes.buttonText)}>
                            {isEventInfluencer ? t('Start streaming') : t('Join streaming')}
                        </Typography>
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default Intro;
